import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { FaVideo, FaMicrophone, FaVideoSlash, FaMicrophoneSlash, FaPhone, FaComments } from "react-icons/fa";
import { SkeletonRoom } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./VideoRoom.css";

export default function VideoRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const socketRef = useRef(null);
  const userId = sessionStorage.getItem("userId") || localStorage.getItem("userId");

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/funtellime/video-session/${sessionId}`);
        const data = await response.json();
        setSessionData(data);
      } catch (error) {
      }
    };

    fetchSession();

    socketRef.current = io(API_URL, {
      transports: ["websocket"]
    });

    socketRef.current.emit("funtellime-video-join", { sessionId, userId });

    socketRef.current.on("funtellime-video-ended", (data) => {
      alert("Your partner has ended the video chat");
      navigate(`/funtellime/video-end/${sessionId}`);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [sessionId, navigate, userId]);

  const handleEndCall = () => {
    if (window.confirm("Are you sure you want to end this video chat?")) {
      socketRef.current?.emit("funtellime-video-end-chat", { sessionId, userId });
      navigate(`/funtellime/video-end/${sessionId}`);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleVideo = () => {
    setIsVideoOff(!isVideoOff);
  };

  if (!sessionData) {
    return (
      <div className="video-room-container">
        <SkeletonRoom />
      </div>
    );
  }

  return (
    <div className="video-room-container">
      {/* I-Sharing Prompt Overlay */}
      {showPrompt && (
        <div className="prompt-overlay">
          <div className="prompt-card">
            <FaComments className="prompt-icon" />
            <h3>Your Conversation Starter</h3>
            <p className="prompt-text">{sessionData.isharingPrompt}</p>
            <button className="close-prompt-btn" onClick={() => setShowPrompt(false)}>
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Video Display Area */}
      <div className="video-grid">
        <div className="video-frame partner-video">
          <div className="video-placeholder">
            <FaVideo className="placeholder-icon" />
            <p>Partner's Video</p>
            <span className="video-label">{sessionData.dynamicProfile2.name}</span>
          </div>
        </div>

        <div className="video-frame my-video">
          <div className="video-placeholder">
            {isVideoOff ? <FaVideoSlash className="placeholder-icon" /> : <FaVideo className="placeholder-icon" />}
            <p>{isVideoOff ? "Camera Off" : "Your Video"}</p>
            <span className="video-label">You</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="video-controls">
        <button 
          className={`control-btn ${isMuted ? "muted" : ""}`}
          onClick={toggleMute}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </button>

        <button 
          className={`control-btn ${isVideoOff ? "video-off" : ""}`}
          onClick={toggleVideo}
          title={isVideoOff ? "Turn on camera" : "Turn off camera"}
        >
          {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
        </button>

        <button 
          className="control-btn prompt-btn"
          onClick={() => setShowPrompt(true)}
          title="Show conversation starter"
        >
          <FaComments />
        </button>

        <button 
          className="control-btn end-call-btn"
          onClick={handleEndCall}
          title="End call"
        >
          <FaPhone />
        </button>
      </div>

      <div className="session-info">
        <p>Video Chat Session • {sessionId.substring(0, 8)}</p>
      </div>
    </div>
  );
}
